import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentWorkspace } from '../../redux/auth/selectors';
import { getSelfCapabilities } from '../../service/capability.service';
import {
  findOrganisationRoot,
  listWorkspaceNodes,
  nodesOfOrganisation,
  type WorkspaceNode
} from '../../service/invitation.service';

export interface OrganisationAccess {
  /** True until the organisation and the caller's right over it have both been read. */
  loading: boolean;
  /** The organisation (root workspace) the active workspace belongs to, when the caller reaches it. */
  organisation?: WorkspaceNode;
  /** The organisation and every workspace beneath it that the caller reaches. */
  nodes: WorkspaceNode[];
  /** MEMBERSHIP create on the organisation itself: the right to invite people into it. */
  canInvite: boolean;
}

const NONE: OrganisationAccess = {
  loading: false,
  nodes: [],
  canInvite: false
};

/**
 * The organisation above the active workspace, and whether the caller may invite people into
 * it.
 *
 * The active workspace's capability row (`useSelfCapabilities`) answers for that workspace
 * only, and the right to invite is held at the organisation, so this reads the organisation's
 * own row: `GET r/v2/workspaces/{rootId}/capabilities/me`, whose MEMBERSHIP cell must be
 * MANAGE (the level that includes create). The organisation is found from the workspaces the
 * caller reaches. Anything that cannot be established - no organisation among them, a refused
 * or failed read - is "no", so a control is hidden rather than drawn to answer 403.
 *
 * With `nodeId` the organisation is resolved from that node (itself, or its root) instead of
 * the active workspace.
 */
export const useOrganisationAccess = (nodeId?: number): OrganisationAccess => {
  const workspace = useSelector(selectCurrentWorkspace);
  // A console passes its own node, so the organisation is the one being administered rather
  // than the one the (possibly absent) active workspace sits in.
  const workspaceId: number | undefined = nodeId ?? workspace?.id;
  const [access, setAccess] = useState<OrganisationAccess>({
    ...NONE,
    loading: true
  });

  useEffect(() => {
    let cancelled = false;
    setAccess({
      ...NONE,
      loading: true
    });
    (async () => {
      try {
        const nodes = await listWorkspaceNodes();
        const organisation = findOrganisationRoot(nodes, workspaceId);
        if (!organisation) return NONE;
        const own = await getSelfCapabilities(organisation.id);
        return {
          loading: false,
          organisation,
          nodes: nodesOfOrganisation(nodes, organisation.id),
          canInvite: own.capabilities?.MEMBERSHIP === 'MANAGE'
        } as OrganisationAccess;
      } catch {
        return NONE;
      }
    })().then((next) => {
      if (!cancelled) setAccess(next);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return access;
};
